<template>
  <div id="reports" class="flex h-full bg-pure-white">
    <div class="flex flex-col flex-grow pt-2">
      <div
        id="report-title"
        class="sticky z-50 flex items-center justify-between top-18 left-6 h-18 bg-pure-white"
        :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
      >
        <div class="flex items-center gap-2">
          <BlTextTitle4 text="Reports" />
          <BlBubbleCount :count="totalReports || 0" />
        </div>
        <div class="flex items-center gap-2">
          <BlSort
            v-model="sortInput"
            :options="sortOptions"
            :defaultValue="defaultSort"
            dropDirection="left"
            @change="onChangeSort($event)"
          />
          <BlInputButton
            icon="plus"
            label="Create report"
            @clicked="$modal.show('modal-create-report')"
          />
        </div>
      </div>

      <NewReportModal />

      <div v-if="fetchLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div
        v-else-if="!reports.length"
        class="flex flex-col items-center justify-center w-full text-center h-128"
      >
        <BlTextBody1
          text="Currently, there are no report to display. Once you create a"
        />
        <div class="flex items-center justify-center gap-2 text-center">
          <BlTextBody1 text="new report, it will appear here." />
          <BlInputButton
            label="Create a new report."
            class="p-0"
            type="plain"
            @clicked="$modal.show('modal-create-report')"
          />
        </div>
      </div>
      <div v-else class="mb-4">
        <div
          id="credit-header"
          class="sticky z-20 flex items-center rounded-t top-36"
        >
          <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
            <BlTooltip
              class="flex items-center flex-shrink-0 h-12 px-4 border-t border-l rounded-tl w-14 bg-slate-300 border-slate-400"
            >
              <BlInputCheck
                :checked="checked"
                :indeterminate="indeterminate"
                @changed="changeSelectedAll"
              />
            </BlTooltip>
            <BlTooltip
              class="flex items-center flex-shrink-0 h-12 px-4 border-t border-r w-60 bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="Report name" class="truncate" />
            </BlTooltip>
          </div>
          <BlTooltip
            class="flex items-center flex-shrink-0 w-40 h-12 px-4 border-t bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Status" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 w-56 h-12 px-4 border-t bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Date range" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 w-56 h-12 px-4 border-t bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Template used" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Created by" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Last updated" class="truncate" />
          </BlTooltip>
          <BlTooltip
            class="flex items-center flex-shrink-0 h-12 px-4 border-t w-50 bg-slate-300 border-slate-400"
          >
            <BlTextSubtitle3 text="Updated by" class="truncate" />
          </BlTooltip>
          <div class="sticky right-0 z-10 flex flex-grow pr-6 bg-pure-white">
            <div
              class="flex items-center flex-grow flex-shrink-0 h-12 px-4 border-t border-r rounded-tr w-60 bg-slate-300 border-slate-400"
            >
              <BlTextSubtitle3 text="" />
            </div>
          </div>
        </div>

        <ClientReportItem
          v-for="(report, idx) in reports"
          :key="report.id"
          :report="report"
          :checked="selectedReport.includes(report.id)"
          :isLast="idx + 1 === reports.length"
          @change-selected="changeSelected(report.id)"
        />

        <div v-if="totalReports > 25" class="flex mb-36">
          <BlInputPagination
            class="mt-6"
            :style="{ width: 'calc(100vw - 3rem - 0.5rem)' }"
            :page="page"
            :perPage="perPage"
            :total="totalReports"
            @changed="changePage"
            @changed-per-page="changePerPage"
          />
        </div>
        <div v-else class="mb-46"></div>
      </div>
    </div>

    <div
      class="fixed z-50 flex items-center justify-between w-full gap-2 px-6 py-2 transition-all duration-500 bg-white border rounded shadow-lg min-h-16 max-w-200 border-slate-400"
      :class="[selectedReport.length ? 'bottom-16' : '-bottom-16']"
      :style="{
        left: '50%',
        transform: 'translateX(-50%)'
      }"
    >
      <div class="flex items-center gap-2 truncate">
        <BlInputButton
          type="plain"
          icon="close"
          @clicked="selectedReport = []"
        />
        <div class="truncate">
          <BlTextSubtitle3
            :text="`${selectedReport.length} report(s) selected`"
            class="text-slate-800"
          />
        </div>
      </div>
      <div v-if="selectedReport.length" class="flex items-center gap-2">
        <BlInputButton
          :label="`Delete (${selectedReport.length})`"
          type="outlined-error"
          dense
          :loading="deleteLoading"
          @clicked="deleteOpened = true"
        />
        <BlInputButton
          v-if="downloadable"
          :label="`Download (${selectedReport.length})`"
          dense
          :loading="downloadLoading"
          @clicked="onDownloadSelected"
        />
      </div>
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete report"
      :label="`Delete ${selectedReport.length} report(s)`"
      type="error"
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDeleteSelected"
    >
      <div class="px-4 lg:px-8">
        <div>
          <BlTextBody2
            text="You are about to delete the following report from your client:"
          />
        </div>
        <div
          v-for="(name, idx) in selectedReportNames"
          :key="idx"
          class="flex items-center gap-2 px-2"
          :title="text"
        >
          <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-800"></div>
          <BlTextBody2 :text="name" class="truncate" />
        </div>
        <div>
          <BlTextBody2
            text="Are you sure you want to proceed? This action is irreversible. "
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ClientReportItem from "./report/Item.vue";
import NewReportModal from "./report/NewReportModal.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlIcon from "../BlIcon.vue";
import BlChip from "../BlChip.vue";
import BlSort from "../BlSort.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlTooltip from "../BlTooltip.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "ClientReport",
  components: {
    ClientReportItem,
    NewReportModal,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlChip,
    BlSort,
    BlBubbleCount,
    BlTooltip,
    BlInputButton,
    BlInputCheck,
    BlInputPagination,
    BlBackdrop
  },
  props: ["id"],
  data() {
    const defaultSort = { primary: "date", secondary: "desc" };
    return {
      defaultSort,
      //
      page: 1,
      perPage: 25,
      fetchLoading: false,
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "date", label: "Date created" },
          { value: "alphabetical", label: "Report name" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      },
      selectedReport: [],
      downloadLoading: false,
      deleteOpened: false,
      deleteLoading: false
    };
  },
  computed: {
    ...mapGetters(["reports", "totalReports", "client"]),
    checked() {
      if (!this.reports) return false;
      return this.reports
        .filter(a => a.status === "published")
        .every(a => this.selectedReport.includes(a.id));
    },
    indeterminate() {
      if (!this.reports) return false;
      return (
        this.reports
          .filter(a => a.status === "published")
          .findIndex(a => this.selectedReport.includes(a.id)) > -1 &&
        !this.checked
      );
    },
    downloadable() {
      if (!this.reports) return false;
      return (
        this.reports.findIndex(
          a => a.status !== "published" && this.selectedReport.includes(a.id)
        ) === -1
      );
    },
    selectedReportNames() {
      if (!this.reports) return [];
      return this.reports
        .filter(a => this.selectedReport.includes(a.id))
        .map(a => a.name);
    }
  },
  mounted() {
    const searchParams = this.$route.query;

    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }

    // prettier-ignore
    const sorts = {
      oldest: { primary: "date", secondary: "asc" },
      newest: { primary: "date", secondary: "desc" },
      alphabetical: { primary: "alphabetical", secondary: "asc" },
      alphabetical_desc: { primary: "alphabetical", secondary: "desc" }
    };
    if (searchParams["sort"]) {
      const sort = searchParams["sort"] || "";
      if (sort && sorts[sort]) {
        this.sortInput = sorts[sort];
      }
    }

    this.fetchReports();
  },
  methods: {
    ...mapActions(["deleteReports", "downloadReports", "getReports"]),
    fetchReports() {
      this.fetchLoading = true;

      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const sort = this.sortInput;

      // - sort mapping -
      // date, ascending -> oldest
      // date, descending -> newest
      // alphabetical, ascending -> alphabetical
      // alphabetical, descending -> alphabetical_desc
      // prettier-ignore
      const sorts = {
        date: { asc: "oldest", desc: "newest" },
        alphabetical: { asc: "alphabetical", desc: "alphabetical_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      this.getReports({ client_id: this.id, params }).then(({ success }) => {
        this.fetchLoading = false;
        // if (!success) return;
      });

      // sync url path for page
      const incomingPath = `/clients/${this.id}/report${params}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchReports();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchReports();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchReports();
    },
    changeSelected(id) {
      if (this.selectedReport.includes(id)) {
        this.selectedReport.splice(this.selectedReport.indexOf(id), 1);
      } else this.selectedReport.push(id);
    },
    changeSelectedAll() {
      if (this.selectedReport.length === this.reports.length) {
        this.selectedReport = [];
      } else if (this.reports) {
        this.selectedReport = this.reports
          .filter(a => a.status === "published")
          .map(a => a.id);
      }
    },
    onDeleteSelected() {
      this.deleteLoading = true;
      const data = {
        ids: this.selectedReport
      };
      this.deleteReports(data).then(({ success }) => {
        this.deleteLoading = false;
        if (!success) return;
        this.selectedReport = [];
        this.deleteOpened = false;
      });
    },
    async onDownloadSelected() {
      this.downloadLoading = true;
      const data = {
        ids: this.selectedReport
      };
      this.downloadReports(data)
        .then(res => {
          this.downloadLoading = false;
          const href = URL.createObjectURL(res.data);

          // generate filename
          function getFormattedTime() {
            const today = new Date();
            const y = today.getFullYear();
            // JavaScript months are 0-based.
            const m = today.getMonth() + 1;
            const d = today.getDate();
            const h = today.getHours();
            const mi = today.getMinutes();
            const s = today.getSeconds();
            return `${y}${m}${d}T${h}${mi}${s}`;
          }

          const currentReport = this.reports.find(
            i => Number(i.id) === Number(this.selectedReport[0])
          );
          const reportName = currentReport ? currentReport.name : "report";
          const clientName = this.client ? this.client.name : "client";
          const suffix = getFormattedTime();
          let filename = `${clientName}_${reportName}_${suffix}.pdf`;
          if (this.selectedReport.length > 1) {
            filename = `reports_${clientName}_${suffix}.zip`;
          }

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          this.selectedReport = [];
        })
        .catch(err => {
          console.error(err);
          this.downloadLoading = false;
          this.selectedReport = [];
        });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/ClientReport.vue